import React, { useState, useEffect } from 'react'
import { Button, Label } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { DocLoader } from './DocLoader/DocLoader'
import { isEmpty, isNil } from 'ramda'
import { UPLOAD_EXT_DOC_API_PATH } from '../../../constants/routes'
import { useStore } from '../../../StateProvider'
import { isEmptyNewDocLoader } from '../utils'
import styles from './style.css'

const DEFAULT_ADDITIONAL_DOCS = { primary: [], joint: [], error: {} }

/**
 * @param {number} min
 * @param {number} max
 * @param {string} label
 * @param {string} name
 * @param {'ALL' | 'ID_CARD' | 'PASSPORT' | 'DRIVING_LICENSE'} showOnDocType
 */
const ExternalUploadDoc = ({ min = 1, max = 1, label, name, showOnDocType = 'PASSPORT' }) => {
  const [{
    additionalDocs = DEFAULT_ADDITIONAL_DOCS,
    form,
    form: { accId },
    flags: { isIdBackSideScan, isDocumentUploaded }
  }, dispatch] = useStore()
  const userType = accId === 0 ? 'primary' : 'joint'
  const additionalDocsCount = additionalDocs[userType].filter(key => !isEmptyNewDocLoader(key)).length
  const { idScan, idBackSideScan } = form[accId]
  const [fieldsNumber, setFieldsNumber] = useState(min)
  const [isShown, setIsShown] = useState(false)
  const [numberFilesUploaded, setNumberFilesUploaded] = useState(additionalDocsCount)
  const { t } = useTranslation()
  const { idType } = form[accId]
  const isBackSide = (isIdBackSideScan === true && !isNil(idBackSideScan) && !isEmpty(idBackSideScan)) || !isIdBackSideScan

  useEffect(() => {
    const value = additionalDocsCount >= min
    dispatch({
      type: 'setFlag',
      name: 'isDocumentUploaded',
      value: { ...isDocumentUploaded, [accId]: value }
    })
  }, [additionalDocs])

  useEffect(() => {
    const value = additionalDocsCount >= min
    if (isShown && !isDocumentUploaded[accId]) {
      dispatch({ type: 'setValidationFld', name, value: value })
    }
  }, [numberFilesUploaded, isShown, min, additionalDocs])

  useEffect(() => {
    if (isDocumentUploaded[accId]) {
      dispatch({ type: 'setValidationFld', name, value: true })
    }
  }, [idType, isDocumentUploaded[accId]])

  useEffect(() => {
    if ((idType === showOnDocType || showOnDocType === 'ALL') && !isNil(idScan) && !isEmpty(idScan) && isBackSide) {
      if (!isShown) {
        dispatch({ type: 'addValidationFld', name })
      }
      setIsShown(true)

      if (additionalDocs[userType].length < min) {
        const diff = min - additionalDocs[userType].length
        dispatch({ type: 'initDefaultFields', count: diff, userType })
        setFieldsNumber(diff)
      } else {
        setNumberFilesUploaded(additionalDocsCount)
      }
    } else if (numberFilesUploaded === 0) {
      setIsShown(false)
      setNumberFilesUploaded(additionalDocsCount)
      setFieldsNumber(min)
    }
  }, [idType, isShown, showOnDocType, idScan, name, isBackSide])

  const addNewField = () => {
    const newFieldNumber = fieldsNumber + 1
    setFieldsNumber(newFieldNumber)
    dispatch({ type: 'addField', id: newFieldNumber, userType })
  }

  const increaseNumberFilesUploaded = (documentId, recIndex) => {
    dispatch({ type: 'addUploadedDoc', id: documentId, recIndex, userType })
    setNumberFilesUploaded(prev => prev + 1)
  }

  const decreaseNumberFilesUploaded = (documentId, error) => {
    if (documentId && error) {
      const newFieldNumber = fieldsNumber + 1
      setFieldsNumber(newFieldNumber)

      dispatch({ type: 'addUploadedDoc', id: newFieldNumber, recIndex: documentId, userType })
      dispatch({ type: 'addUploadedDocError', id: newFieldNumber, error, userType })
    }
    if (numberFilesUploaded >= 1) {
      setNumberFilesUploaded(prev => prev - 1)
    }
  }

  const onRemoveUploadedHandler = (documentId) => {
    dispatch({ type: 'removeUploadedDoc', documentId, userType })
    setNumberFilesUploaded(prev => prev - 1)
  }

  const isValid = isDocumentUploaded[accId] || additionalDocsCount >= min
  const value = isDocumentUploaded[accId] ? t('generatedField.uploadedDocumentsLabel') : t(label)

  return (
    isShown &&
      <div className={styles.block}>
        <Label
          value={value}
          valid={isValid}
          className={styles.label}
          info={t('generatedField.uploadDocumentsInfo')}
        />
        <Button onClick={addNewField} className={styles.button} disabled={additionalDocs[userType].length === max}>
          {t('generatedField.addDocument')}
        </Button>
        <div>
          {additionalDocs[userType].map((recIndex, index) =>
            <UploadDocItem
              recIndex={recIndex}
              index={index}
              key={`${recIndex}/${index}`}
              onUpload={increaseNumberFilesUploaded}
              onReject={decreaseNumberFilesUploaded}
              onRemoveUploaded={onRemoveUploadedHandler}
            />)}
        </div>
      </div>
  )
}

const UploadDocItem = ({ recIndex, index, onUpload, onReject, onRemoveUploaded }) => {
  const [{
    form: { accId }
  }] = useStore()

  const isJointUserType = accId > 0

  return (
    <DocLoader
      recIndex={recIndex}
      label={`#${index + 1}`}
      name={index.toString()}
      url={`${UPLOAD_EXT_DOC_API_PATH}?joint=${isJointUserType}`}
      error=''
      className=''
      onUpload={onUpload}
      onReject={onReject}
      onRemoveUploaded={onRemoveUploaded}
    />
  )
}

const getExternalDocsField = docsFld => {
  const { i18n, name, min, max, order, showOnDocType } = docsFld

  return (
    <ExternalUploadDoc
      min={min}
      max={max}
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      order={order}
      showOnDocType={showOnDocType}
      key={name}
    />
  )
}

export { ExternalUploadDoc, getExternalDocsField }
